import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../styles/DailyQuests.css';

const DailyQuests = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [questData, setQuestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuestData(true); // Show loading on initial load
      
      // Listen for daily check-in completion
      const handleCheckInComplete = () => {
        console.log('Daily check-in completed, refreshing quests...');
        setTimeout(() => fetchQuestData(false), 1000); // Silent refresh
      };
      
      window.addEventListener('dailyCheckInComplete', handleCheckInComplete);
      
      // Refresh quests every 30 seconds to catch updates (silent)
      const interval = setInterval(() => {
        fetchQuestData(false);
      }, 30000);
      
      return () => {
        window.removeEventListener('dailyCheckInComplete', handleCheckInComplete);
        clearInterval(interval);
      };
    }
  }, [isAuthenticated]);

  const fetchQuestData = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/daily-rewards/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestData(response.data.data);
      setError('');
    } catch (err) {
      console.error('Error fetching quest data:', err);
      setError('Failed to load daily quests');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="daily-quests-widget guest">
        <div className="quests-header">
          <h3>📋 Daily Quests</h3>
          <p>Login to start completing quests!</p>
        </div>
        <Link to="/login" className="quest-login-button">
          Log In to Start
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="daily-quests-widget loading">
        <div className="spinner-small"></div>
      </div>
    );
  }

  if (error && !questData) {
    return (
      <div className="daily-quests-widget error">
        <p>{error}</p>
        <button onClick={fetchQuestData} className="retry-btn">Retry</button>
      </div>
    );
  }

  const quests = questData?.dailyQuests || [];
  const completedCount = quests.filter(q => q.completed).length;
  const totalPoints = quests.reduce((sum, q) => sum + (q.completed ? q.pointsEarned : 0), 0);

  return (
    <div className="daily-quests-widget">
      <div className="quests-header">
        <h3>📋 Daily Quests</h3>
        <div className="quests-progress">
          <span className="progress-text">{completedCount}/{quests.length} Complete</span>
          <span className="points-earned">+{totalPoints} pts</span>
        </div>
      </div>

      <div className="quests-list">
        {quests.map((quest) => {
          const progressPercent = (quest.progress.current / quest.progress.required) * 100;
          
          return (
            <div 
              key={quest.questId} 
              className={`quest-item ${quest.completed ? 'completed' : ''}`}
            >
              <div className="quest-icon">{quest.icon}</div>
              <div className="quest-details">
                <div className="quest-name">{quest.name}</div>
                <div className="quest-description">{quest.description}</div>
                <div className="quest-progress-bar">
                  <div 
                    className="quest-progress-fill" 
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
                <div className="quest-progress-text">
                  {quest.progress.current}/{quest.progress.required}
                </div>
              </div>
              <div className="quest-reward">
                {quest.completed ? (
                  <div className="quest-complete-icon">✅</div>
                ) : (
                  <div className="quest-points">+{quest.points}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {completedCount === quests.length && quests.length > 0 && (
        <div className="all-complete-banner">
          <span className="banner-icon">🎉</span>
          <span className="banner-text">All quests complete! Come back tomorrow!</span>
        </div>
      )}

      <div className="quests-footer">
        <span className="reset-info">⏰ Resets at midnight</span>
        <Link to="/points-guide" className="view-guide-link">
          View Points Guide →
        </Link>
      </div>
    </div>
  );
};

export default DailyQuests;
